// src/mobile_pages/system/User.jsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../../api/axios";
import SignatureForm from "../../components/forms/SignatureForm";
import { User as UserIcon, Building2, Mail, Phone, Lock, FileSignature } from "lucide-react";

const RE = {
  username: /^[a-zA-Z0-9._-]{3,20}$/,
  password: /^(?=.*[a-z])(?=.*\d).{6,64}$/,
  email: /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/,
  phone: /^(01[016789])[-]?\d{3,4}[-]?\d{4}$/,
};

const formatPhoneKR = (raw) => {
  let digits = (raw || "").replace(/\D/g, "");
  if (digits.length === 10 && digits.startsWith("1")) digits = "0" + digits;
  if (digits.length > 11) digits = digits.slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7, 11)}`;
};

async function fetchAll(url) {
  const acc = [];
  let next = url;
  while (next) {
    const { data } = await api.get(next);
    const items = Array.isArray(data)
      ? data
      : Array.isArray(data.results)
      ? data.results
      : [];
    acc.push(...items);
    next = data.next || null;
  }
  return acc;
}

function normalizeMemberships(raw) {
  if (Array.isArray(raw)) return raw;
  if (raw && Array.isArray(raw.results)) return raw.results;
  if (raw && Array.isArray(raw.memberships)) return raw.memberships;
  return [];
}

export default function User() {
  const { userId } = useParams();
  const isMyProfile = !userId;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    username: "",
    email: "",
    phone_number: "",
    first_name: "",
    last_name: "",
  });

  const [org, setOrg] = useState({
    company_name: "",
    department_name: "",
    position_name: "",
  });

  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [passwordVerified, setPasswordVerified] = useState(false);
  const [passwords, setPasswords] = useState({
    current_password: "",
    password: "",
    confirmPassword: "",
  });

  const [signaturePreview, setSignaturePreview] = useState(null);
  const [signatureFile, setSignatureFile] = useState(null);

  const nameOf = (arr, id) =>
    id ? arr.find((x) => Number(x.id) === Number(id))?.name ?? "" : "";

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError("");

        const [companies, departments, positions] = await Promise.all([
          fetchAll("companies/"),
          fetchAll("departments/"),
          fetchAll("positions/"),
        ]);

        let profileData = {};
        let membershipData = [];

        if (isMyProfile) {
          const [{ data: profile }, { data: memRaw }] = await Promise.all([
            api.get("profile/"),
            api.get("membership/me/"),
          ]);
          profileData = profile;
          membershipData = normalizeMemberships(memRaw);
        } else {
          const [{ data: profile }, { data: memRaw }] = await Promise.all([
            api.get(`users/${userId}/`),
            api.get(`users/${userId}/memberships/`),
          ]);
          profileData = profile;
          membershipData = normalizeMemberships(memRaw);
        }

        setForm({
          username: profileData.username || "",
          email: profileData.email || "",
          phone_number: profileData.phone_number || "",
          first_name:
            profileData.first_name ??
            profileData.given_name ??
            profileData.name?.split(" ")?.slice(-1).join(" ") ??
            "",
          last_name:
            profileData.last_name ??
            profileData.family_name ??
            profileData.name?.split(" ")?.[0] ??
            "",
        });

        const primary =
          membershipData.find((m) => m.is_primary) || membershipData[0] || null;

        setOrg({
          company_name:
            primary?.company_name || nameOf(companies, primary?.company) || "",
          department_name:
            primary?.department_name ||
            nameOf(departments, primary?.department) ||
            "",
          position_name:
            primary?.position_name ||
            nameOf(positions, primary?.position) ||
            "",
        });

        setSignaturePreview(profileData.sign_file || null);
        setSignatureFile(null);
      } catch (e) {
        console.error("[User] load error:", e);
        setError(
          isMyProfile
            ? "내 정보를 불러오지 못했습니다."
            : "사용자 정보를 불러오지 못했습니다."
        );
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [userId, isMyProfile]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const onChange = (e) => set(e.target.name, e.target.value);
  const onChangePhone = (e) =>
    set("phone_number", formatPhoneKR(e.target.value));
  const onChangePw = (e) =>
    setPasswords((p) => ({ ...p, [e.target.name]: e.target.value }));

  const verifyCurrentPassword = async () => {
    try {
      await api.put("profile/", {
        current_password: passwords.current_password,
      });
      setPasswordVerified(true);
      alert("비밀번호 인증 성공. 새 비밀번호를 입력하세요.");
    } catch {
      alert("현재 비밀번호가 올바르지 않습니다.");
    }
  };

  const validate = () => {
    if (
      !form.username ||
      !form.email ||
      !form.phone_number ||
      !form.first_name ||
      !form.last_name
    ) {
      setError("필수값 누락");
      return false;
    }
    if (!RE.username.test(form.username))
      return setError("username 형식 오류"), false;
    if (!RE.email.test(form.email)) return setError("email 형식 오류"), false;
    if (!RE.phone.test(form.phone_number))
      return setError("phone 형식 오류"), false;
    if (isMyProfile && passwordVerified && passwords.password) {
      if (!RE.password.test(passwords.password))
        return setError("password 형식 오류 (소문자+숫자 포함 6~64자)"), false;
      if (passwords.password !== passwords.confirmPassword)
        return setError("새 비밀번호와 확인이 일치하지 않습니다."), false;
    }
    setError("");
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const basePayload = {
      email: form.email,
      phone_number: form.phone_number,
      first_name: form.first_name,
      last_name: form.last_name,
    };

    try {
      if (isMyProfile) {
        const payload =
          passwordVerified && passwords.password
            ? {
                ...basePayload,
                password: passwords.password,
                current_password: passwords.current_password,
              }
            : basePayload;

        await api.put("profile/", payload);

        if (signatureFile) {
          const fd = new FormData();
          fd.append("file", signatureFile, "signature.png");
          const res = await api.post("/me/signature/", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          setSignaturePreview(res.data?.signature_url || null);
          setSignatureFile(null);
        }
      } else {
        await api.put(`users/${userId}/`, basePayload);
      }

      alert("수정 완료");

      if (isMyProfile) {
        setPasswords({
          current_password: "",
          password: "",
          confirmPassword: "",
        });
        setPasswordVerified(false);
        setShowPasswordFields(false);
      }
      setError("");
    } catch (err) {
      console.error("[User] save error:", err);
      setError(err?.response?.data?.detail || "수정 중 오류가 발생했습니다.");
    }
  };

  // Skeleton Loading Card
  const SkeletonCard = () => (
    <div className="section-card p-4 space-y-3 animate-pulse">
      <div className="h-4 bg-gray-200 rounded w-1/3"></div>
      <div className="h-10 bg-gray-200 rounded"></div>
      <div className="h-10 bg-gray-200 rounded"></div>
    </div>
  );

  if (loading) {
    return (
      <div className="mobile-page-root">
        <div className="mobile-page-root-inner space-y-4 mobile-bottom-spacing">
          <div className="mobile-page-header">
            <h1 className="mobile-page-title">
              {isMyProfile ? "내 정보" : "사용자 상세 정보"}
            </h1>
          </div>
          <SkeletonCard />
          <SkeletonCard />
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-page-root">
      <div className="mobile-page-root-inner space-y-4 mobile-bottom-spacing pb-24">
        {/* Header */}
        <div className="mobile-page-header">
          <h1 className="mobile-page-title">
            {isMyProfile ? "내 정보" : "사용자 상세 정보"}
          </h1>
        </div>

        {/* Profile Summary Card */}
        <div className="section-card p-4">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 font-bold text-lg flex-shrink-0">
              {(form.last_name || "U").slice(0, 1)}
            </div>
            <div className="min-w-0">
              <p className="text-base font-semibold text-gray-900 truncate">
                {form.last_name}{form.first_name}
              </p>
              <p className="text-xs text-gray-500 truncate">{form.email}</p>
              <div className="flex gap-2 mt-1 text-[10px] text-gray-400">
                <span>{org.company_name || "-"}</span>
                <span>·</span>
                <span>{org.department_name || "-"}</span>
                <span>·</span>
                <span>{org.position_name || "-"}</span>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Account Info Card */}
          <div className="section-card p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
              <UserIcon className="w-4 h-4" />
              <span>계정 정보</span>
            </div>
            
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">성(Last Name)*</label>
                <input
                  name="last_name"
                  value={form.last_name}
                  onChange={onChange}
                  className="w-full rounded-md border px-3 py-2 text-sm"
                  placeholder="성"
                />
              </div>

              <div>
                <label className="text-xs text-gray-500 mb-1 block">이름(First Name)*</label>
                <input
                  name="first_name"
                  value={form.first_name}
                  onChange={onChange}
                  className="w-full rounded-md border px-3 py-2 text-sm"
                  placeholder="이름"
                />
              </div>

              <div>
                <label className="text-xs text-gray-500 mb-1 block">아이디(변경 불가)</label>
                <input
                  name="username"
                  value={form.username}
                  disabled
                  className="w-full rounded-md border px-3 py-2 text-sm bg-gray-50"
                />
              </div>

              <div>
                <label className="text-xs text-gray-500 mb-1 block flex items-center gap-1">
                  <Mail className="w-3 h-3" />
                  이메일*
                </label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  className="w-full rounded-md border px-3 py-2 text-sm"
                  placeholder="user@example.com"
                />
              </div>

              <div>
                <label className="text-xs text-gray-500 mb-1 block flex items-center gap-1">
                  <Phone className="w-3 h-3" />
                  전화번호*
                </label>
                <input
                  name="phone_number"
                  value={form.phone_number}
                  onChange={onChangePhone}
                  className="w-full rounded-md border px-3 py-2 text-sm"
                  placeholder="010-1234-5678"
                />
              </div>
            </div>
          </div>

          {/* Organization Card */}
          <div className="section-card p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
              <Building2 className="w-4 h-4" />
              <span>조직 정보</span>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-xs text-gray-500">회사</span>
                <span className="text-sm text-gray-800">{org.company_name || "-"}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-xs text-gray-500">부서</span>
                <span className="text-sm text-gray-800">{org.department_name || "-"}</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-xs text-gray-500">직위</span>
                <span className="text-sm text-gray-800">{org.position_name || "-"}</span>
              </div>
            </div>
          </div>

          {/* Password Change Card (My Profile Only) */}
          {isMyProfile && (
            <div className="section-card p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                  <Lock className="w-4 h-4" />
                  <span>비밀번호 변경</span>
                </div>
                {!showPasswordFields && (
                  <button
                    type="button"
                    onClick={() => setShowPasswordFields(true)}
                    className="btn-basic-sm text-xs"
                  >
                    변경
                  </button>
                )}
              </div>

              {showPasswordFields && (
                <div className="space-y-3">
                  {!passwordVerified ? (
                    <>
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">
                          현재 비밀번호 확인
                        </label>
                        <input
                          type="password"
                          name="current_password"
                          placeholder="현재 비밀번호"
                          value={passwords.current_password}
                          onChange={onChangePw}
                          className="w-full rounded-md border px-3 py-2 text-sm"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={verifyCurrentPassword}
                        className="btn-basic-sm text-xs w-full"
                      >
                        비밀번호 인증
                      </button>
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">
                          새 비밀번호
                        </label>
                        <input
                          type="password"
                          name="password"
                          placeholder="소문자+숫자 포함 6~64자"
                          value={passwords.password}
                          onChange={onChangePw}
                          className="w-full rounded-md border px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">
                          비밀번호 확인
                        </label>
                        <input
                          type="password"
                          name="confirmPassword"
                          placeholder="비밀번호 확인"
                          value={passwords.confirmPassword}
                          onChange={onChangePw}
                          className={`w-full rounded-md border px-3 py-2 text-sm ${
                            passwords.confirmPassword.length === 0
                              ? ""
                              : passwords.confirmPassword === passwords.password
                              ? "border-green-500"
                              : "border-red-500"
                          }`}
                        />
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Signature Card */}
          <div className="section-card p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
              <FileSignature className="w-4 h-4" />
              <span>서명</span>
            </div>
            
            {isMyProfile ? (
              <SignatureForm
                value={signaturePreview}
                onChange={(previewUrl, fileOrBlob) => {
                  setSignaturePreview(previewUrl || null);
                  setSignatureFile(fileOrBlob || null);
                }}
              />
            ) : (
              <div className="border rounded-md p-4 flex justify-center bg-gray-50">
                {signaturePreview ? (
                  <img
                    src={signaturePreview}
                    alt="User Signature"
                    className="h-20 object-contain"
                  />
                ) : (
                  <span className="text-gray-400 text-sm">
                    등록된 서명이 없습니다.
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-600">
              {error}
            </div>
          )}

          {/* Submit Button - Fixed at Bottom */}
          <div className="fixed bottom-0 left-0 right-0 p-3 bg-white border-t shadow-lg bottom-nav-mobile-inner">
            <div className="max-w-md mx-auto">
              <button type="submit" className="btn-save w-full py-3">
                저장
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
